/**
 * @fileoverview Put a second instructor on a class — or hand it over.
 *
 * The same dialog does both, because they are the same act seen from two
 * distances: somebody new starts teaching this class, and the question that
 * follows is whether the person already on it carries on. So the name is
 * chosen first and the handover is a checkbox under it, ticked by default when
 * the class already has an instructor — a replacement is the commoner case,
 * and co-teaching is the deliberate one.
 *
 * The candidates are the ORG's instructors, not only the program's, for the
 * reason the assign dialog already documents: an org's instructors are not
 * automatically on any one program, so a list drawn from program membership is
 * empty on a new program and refuses every name it could have offered. Naming
 * someone the org employs joins them to the program on the way.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button } from "@/components/studio/primitives";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

export default function AddClassInstructorDialog({
  open,
  onOpenChange,
  cls,
  current = [],
  programInstructors = [],
  onAdded,
}) {
  const [picked, setPicked] = useState("");
  const [replace, setReplace] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgInstructors, setOrgInstructors] = useState([]);

  const active = useMemo(() => current.filter((i) => i.status !== "inactive"), [current]);

  useEffect(() => {
    if (!open) return;
    setPicked("");
    // A class with nobody on it has nothing to hand over; one that is already
    // taught usually does.
    setReplace(active.length > 0);
  }, [open, active.length]);

  /* Failing quietly is right: the program's own members are already in hand,
     and losing the wider list must not take the dialog down with it. */
  const loadOrgInstructors = useCallback(async () => {
    if (!open) return;
    try {
      const res = await orgApi.get("/org/instructors");
      setOrgInstructors(res.data?.instructors || []);
    } catch {
      setOrgInstructors([]);
    }
  }, [open]);

  useEffect(() => { loadOrgInstructors(); }, [loadOrgInstructors]);

  /** Program members first — they are the likely answer. Anyone already
      teaching the class is not offered; they are already the answer. */
  const choices = useMemo(() => {
    const already = new Set(active.map((i) => i.user_id));
    const memberIds = new Set(programInstructors.map((i) => i.user_id));
    const rows = programInstructors
      .filter((i) => !already.has(i.user_id))
      .map((i) => ({ user_id: i.user_id, name: i.name, onProgram: true }));
    for (const i of orgInstructors) {
      if (already.has(i.user_id) || memberIds.has(i.user_id)) continue;
      rows.push({ user_id: i.user_id, name: i.name || i.email, onProgram: false });
    }
    return rows;
  }, [active, programInstructors, orgInstructors]);

  const chosen = choices.find((c) => c.user_id === picked);
  const leaving = replace ? active : [];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.post(`/org/classes/${cls.class_id}/instructors`, {
        instructor_id: picked,
        replace_active: replace,
      });
      const moved = res.data?.slots_moved || 0;
      toast.success(
        replace && leaving.length
          ? `${chosen?.name} has taken ${cls.name} over${moved ? ` — ${moved} scheduled meeting${moved === 1 ? "" : "s"} moved too` : ""}`
          : `${chosen?.name} is teaching ${cls.name}`,
      );
      onAdded?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't add that instructor"));
    } finally {
      setSaving(false);
    }
  };

  if (!cls) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Who else teaches {cls.name}?</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="add-class-instructor-form">
          {choices.length === 0 ? (
            <p data-testid="add-instructor-none" className="font-sans text-[15px] text-[var(--text-tertiary)]">
              Everyone in your organization who could take this class is already on it. Invite an instructor first.
            </p>
          ) : (
            <>
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                aria-label="Instructor"
                data-testid="add-instructor-pick"
                className={inputClass}
              >
                <option value="">Choose an instructor</option>
                {choices.map((i) => (
                  <option key={i.user_id} value={i.user_id}>
                    {i.onProgram ? i.name : `${i.name} — not on this program yet`}
                  </option>
                ))}
              </select>
              {chosen && !chosen.onProgram && (
                <p data-testid="add-instructor-will-join" className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                  {chosen.name} will be added to this program.
                </p>
              )}

              {active.length > 0 && (
                <label className="mt-4 flex items-start gap-3 font-sans text-[14px] text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={replace}
                    onChange={(e) => setReplace(e.target.checked)}
                    data-testid="add-instructor-replace"
                    className="mt-[3px] accent-[var(--accent-yellow)]"
                  />
                  <span>
                    {active.length === 1
                      ? `${active[0].name} stops teaching this class`
                      : `${active.map((i) => i.name).join(" and ")} stop teaching this class`}
                    <span className="mt-1 block font-sans text-[13px] text-[var(--text-tertiary)]">
                      {replace
                        ? "They keep the lessons they already wrote, and the class leaves their calendar. Untick to have them carry on teaching it together."
                        : "Both instructors see the class and its meetings, and either can write a lesson up."}
                    </span>
                  </span>
                </label>
              )}
            </>
          )}

          <div className="mt-5 flex items-center gap-3">
            <Button type="submit" disabled={saving || !picked} data-testid="add-instructor-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
